import React, { useState, useRef } from 'react';
import './PhotoEditor.css';

const PhotoEditor = ({ imageSrc, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    setOffsetX(offsetX + deltaX);
    setOffsetY(offsetY + deltaY);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsSaving(true);
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = imageSrc;

    img.onload = async () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      ctx.translate(centerX, centerY);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom, zoom);
      ctx.drawImage(img, offsetX / zoom - img.width / 2, offsetY / zoom - img.height / 2);
      ctx.restore();

      canvas.toBlob(async (blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          try {
            await onSave(reader.result);
          } catch (err) {
            console.error('Failed to save photo:', err);
          } finally {
            setIsSaving(false);
          }
        };
      });
    };
  };

  return (
    <div className="photo-editor-overlay">
      <div className="photo-editor-modal">
        <h3>Edit Photo</h3>
        
        <div 
          className="photo-editor-canvas-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img 
            src={imageSrc} 
            alt="Edit" 
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              transform: `rotate(${rotation}deg) scale(${zoom})`,
              cursor: isDragging ? 'grabbing' : 'grab',
              transition: isDragging ? 'none' : 'transform 0.2s ease'
            }}
          />
        </div>

        <canvas
          ref={canvasRef}
          width={300}
          height={300}
          style={{ display: 'none' }}
        />

        <div className="photo-editor-controls">
          <div className="control-group">
            <label>Zoom: {zoom.toFixed(1)}x</label>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
            />
          </div>

          <div className="control-group">
            <label>Rotate: {rotation}°</label>
            <div className="rotation-buttons">
              <button onClick={() => setRotation((rotation - 90) % 360)}>↻ Left</button>
              <button onClick={() => setRotation((rotation + 90) % 360)}>Right ↺</button>
            </div>
          </div>
        </div>

        <div className="photo-editor-actions">
          <button className="btn-cancel" onClick={onCancel} disabled={isSaving}>Cancel</button>
          <button className="btn-save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <span className="spinner"></span> : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditor;
